import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { cardsState } from './setup/data/payments';
import { useStripeStatus } from '../settings/stripeStatus';
import { useOnboarding } from './ImportJobContext';
import { hairdresserService } from '../../_services/hairdresser.service';

/**
 * "Finish setting up" (ONBOARDING-FIRST-RUN-PLAN.md, Step G).
 *
 * The jobs that are worth doing but are not needed to take a first booking.
 * They wait here until the owner has time, instead of crowding the first ten
 * minutes. Each row ticks itself from what the account already holds, so the
 * list is never a lie: connect payments ticks when Stripe is live, the team
 * row ticks when somebody else is on the books, and so on.
 *
 * Rows with `kind: 'action'` have no state worth computing. They stay as
 * suggestions rather than pretending to be a checklist item.
 */

export const FINISH_ITEMS = [
  {
    key: 'payments',
    kind: 'auto',
    title: 'Connect card payments',
    blurb: 'Take cards in the salon and online. Self-employed takings split at the till.',
    cta: 'Connect',
    path: '/settings/payout-account',
  },
  {
    key: 'team',
    kind: 'auto',
    title: 'Add your team',
    blurb: 'Employed people are included in your plan. Self-employed are added per person.',
    cta: 'Open Team',
    path: '/staff',
  },
  {
    key: 'clients',
    kind: 'auto',
    title: 'Bring your client list in',
    blurb: 'Upload a spreadsheet or an export from your old software and we will read it.',
    cta: 'Import',
    path: '/clients',
  },
  {
    key: 'photos',
    kind: 'auto',
    title: 'Add your logo and photos',
    blurb: 'These are what clients see on your booking page and in confirmation emails.',
    cta: 'Open Business',
    path: '/settings/salon-details',
  },
  {
    key: 'policy',
    kind: 'action',
    title: 'Set deposits and a cancellation policy',
    blurb: 'Protect the diary against late cancellations and no-shows.',
    cta: 'Open Bookings',
    path: '/settings/booking-url',
  },
  {
    key: 'client',
    kind: 'action',
    title: 'Look inside a client record',
    blurb: 'History, notes, forms and spend all sit in one place.',
    cta: 'Open Clients',
    path: '/clients',
  },
];

const countFrom = (res) => {
  const d = (res && res.data && (res.data.responseData || res.data.data)) || {};
  if (typeof d.total === 'number') return d.total;
  if (d.pagination && typeof d.pagination.total === 'number') return d.pagination.total;
  if (Array.isArray(d.clients)) return d.clients.length;
  return null;
};

/**
 * The list with each row's state resolved. `done` is true, false, or null
 * when we could not tell, and a row we cannot tell about never shows a tick.
 */
export function useFinishSetup(enabled = true) {
  const user = useSelector((s) => s.user);
  const ob = useOnboarding();
  const stripe = useStripeStatus(null, enabled);
  const bankAccount = useSelector((st) => st.bankDetails);
  const [teamCount, setTeamCount] = useState(null);
  const [clientCount, setClientCount] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let live = true;
    const hairdresserId = localStorage.getItem('userId');
    hairdresserService.staffListServiceV2WithoutService({ hairdresserId, page: 1, listType: 1 })
      .then((res) => {
        const list = (res && res.data && res.data.data && res.data.data.total) || [];
        if (live) setTeamCount(Array.isArray(list) ? list.length : null);
      })
      .catch(() => { if (live) setTeamCount(null); });
    hairdresserService.getClientsPaginated({ page: 1, limit: 1, sortField: 'name', sortOrder: 'asc' })
      .then((res) => { if (live) setClientCount(countFrom(res)); })
      .catch(() => { if (live) setClientCount(null); });
    return () => { live = false; };
  }, [enabled]);

  const salon = (user && user.userInfo && user.userInfo.hairdresser && user.userInfo.hairdresser.salon) || {};
  const tasks = (ob && ob.onboarding && ob.onboarding.checklist && ob.onboarding.checklist.tasks) || {};

  const state = {
    // Cards from charges_enabled, not the derived key; unknown stays open.
    payments: stripe.loading || cardsState(stripe, bankAccount) === 'unknown' ? null : cardsState(stripe, bankAccount) === 'live',
    team: teamCount == null ? null : teamCount > 1,
    clients: clientCount == null ? null : clientCount > 0,
    photos: salon.imageUrl || salon.salonImage ? true : false, // eslint-disable-line no-unneeded-ternary
    policy: null,
    client: tasks.client === 'done' ? true : null,
  };

  const items = FINISH_ITEMS.map((i) => ({ ...i, done: state[i.key] }));
  const counted = items.filter((i) => i.done !== null);
  return {
    items,
    doneCount: counted.filter((i) => i.done).length,
    countable: counted.length,
    loading: stripe.loading && teamCount == null && clientCount == null,
  };
}
